import {
  getConsentErrorMessage,
  setPurposeErrorVisible,
  setSubmissionErrorVisible
} from "./one-trust-consent/errors";
import {
  captureAnalyticsCollectionPoint,
  captureOneTrustFieldValues,
  waitForPhoneInputSync
} from "./one-trust-consent/field-capture";
import {
  predictFormsSubmit,
  takeSubmitAction,
  trackSubmitActions
} from "./one-trust-consent/forms-parity";
import {
  hasCapturedConsent,
  hasCapturedSubmissionConsent,
  submitOneTrustConsentCapture
} from "./one-trust-consent/submit-capture";
import {
  CaptureReason,
  countReportedFailures,
  trackConsentCapture,
  trackFormSubmitted
} from "./one-trust-consent/telemetry";
import {
  registerOneTrustConsentValidator,
  registerOneTrustContactRequiredValidator,
  registerPhoneNumberE164Validator
} from "./one-trust-consent/validators";

type MarsSsoCcSdkInstance = NonNullable<Window["marsSsoCcSdk"]>["marsSsoCcSdk"];

// Reports the Forms Submit outcome for forms with a OneTrust touchpoint (OneTrustFormSubmitted),
// so a consent captured in onBeforeSubmit can be matched with whether the submission itself then
// succeeded - consent is captured *before* the Submit, so the two can diverge (e.g. a server-side
// reCAPTCHA rejection).
const trackFormSubmitOutcomes = (): void => {
  const getOneTrustForm = (event: Event): HTMLFormElement | null => {
    const form = event.currentTarget;

    return form instanceof HTMLFormElement &&
      form.querySelector("[data-ssocc-touchpoint]")
      ? form
      : null;
  };

  window.$$epiforms?.(".EPiServerForms").on("formsSubmitted", (event: any) => {
    const form = getOneTrustForm(event);

    if (form) {
      const validationInfo: { validator?: string }[] =
        event.returnedResult?.data?.validationInfo ?? [];

      trackFormSubmitted(form, {
        outcome: event.isSuccess === true ? "Succeeded" : "ServerRejected",
        isFinalized: event.isFinalizedSubmission,
        consentCaptured: hasCapturedConsent(form.id),
        submissionConsentCaptured: hasCapturedSubmissionConsent(form.id),
        // Validator type names only (e.g. ReCaptchaValidator), never the submitted values.
        serverValidators: validationInfo
          .map(info => info.validator?.split(".").pop())
          .filter((name): name is string => !!name)
      });
    }
  });

  window.$$epiforms?.(".EPiServerForms").on("formsSubmittedError", (event: any) => {
    const form = getOneTrustForm(event);

    if (form) {
      trackFormSubmitted(form, {
        outcome: "RequestFailed",
        consentCaptured: hasCapturedConsent(form.id),
        submissionConsentCaptured: hasCapturedSubmissionConsent(form.id),
        httpStatus: event.xhr?.status
      });
    }
  });
};

// Use the SDK's own default renderer instead of a custom one - a custom renderer is responsible
// for implementing implicit/required/checked/disabled attributes itself, and getting that wrong
// (as happened here) means implicit consents don't end up as checked checkboxes and are never
// sent to the Consent API. The built-in renderer already handles this correctly, so we style its
// markup (`.ssocc-sdk-purpose*`) via onetrust.css instead.
const renderConsentTouchpoint = async (
  marker: HTMLElement,
  marsSsoCcSdk: MarsSsoCcSdkInstance
): Promise<void> => {
  const touchpoint = marker.dataset.ssoccTouchpoint;

  if (touchpoint && marsSsoCcSdk) {
    await marsSsoCcSdk.renderTouchpoint(touchpoint, {
      container: marker
    });

    marker.querySelectorAll<HTMLElement>(".ssocc-sdk-purpose").forEach(purposeElement => {
      const checkbox = purposeElement.querySelector<HTMLInputElement>(
        'input[type="checkbox"]'
      );

      if (checkbox) {
        const error = document.createElement("p");

        error.className = "hidden text-t-sm font-t-semi-bold text-error-200 mt-xs";
        error.setAttribute("data-ssocc-error", "");
        error.setAttribute("role", "alert");
        purposeElement.append(error);

        checkbox.addEventListener("change", () => {
          if (checkbox.checked) {
            setPurposeErrorVisible(purposeElement, false);
          }
        });
      }
    });
  }
};

// How long a submit waits for the SDK to initialize and its form's touchpoint to render, when the
// visitor submits before they have - normally both are done long before a form is filled in.
const SDK_READY_TIMEOUT_MS = 10_000;

type SdkReady = {
  marsSsoCcSdk: MarsSsoCcSdkInstance;
  // Kept per touchpoint, so one touchpoint failing to render only blocks its own form.
  renderedTouchpoints: Map<HTMLElement, Promise<void>>;
};

// The SDK can't be used on this page, for `reason`.
class SdkUnavailableError extends Error {
  constructor(
    readonly reason: CaptureReason,
    readonly detail?: unknown
  ) {
    super(`OneTrust consent SDK unavailable: ${reason}`);
  }
}

// The SDK init/touchpoint render a submit waited on still hadn't settled, for `reason`.
class SdkTimeoutError extends Error {
  constructor(readonly reason: CaptureReason) {
    super(`OneTrust consent SDK didn't settle in time: ${reason}`);
  }
}

const rejectAfter = (ms: number, reason: CaptureReason): Promise<never> =>
  new Promise((_, reject) => {
    setTimeout(() => reject(new SdkTimeoutError(reason)), ms);
  });

// Rejects with an SdkUnavailableError when the global is missing (e.g. blocked by an ad blocker),
// document.documentElement.lang is malformed, or marsSsoCcSdk.init() rejects. Never settles if
// init() never does - a submit only waits on it for SDK_READY_TIMEOUT_MS.
const initializeSdk = async (): Promise<NonNullable<MarsSsoCcSdkInstance>> => {
  const marsSsoCcSdk = window.marsSsoCcSdk?.marsSsoCcSdk;

  if (!marsSsoCcSdk) {
    throw new SdkUnavailableError("SdkUnavailable");
  }

  const [lang, countryCode] = document.documentElement.lang.split("-");

  if (!lang || !countryCode) {
    console.warn(
      "Cannot initialize OneTrust consent tracking: document.documentElement.lang is not in the expected format (e.g. 'en-US')."
    );

    throw new SdkUnavailableError("InvalidPageLanguage");
  }

  try {
    await marsSsoCcSdk.init({
      lang,
      countryCode,
      brand: "ANICURA"
    });
  } catch (e) {
    console.warn("OneTrust consent SDK failed to initialize:", e);

    throw new SdkUnavailableError("SdkInitFailed", e);
  }

  return marsSsoCcSdk;
};

// Forms awaits this via $.when(...).then(...) before finalizing/navigating for the current step
// (see epi-forms.ts), so this is where the OneTrust consent capture call must be submitted and
// awaited - by formsSubmitted, Forms has already finalized and the browser is free to cancel any
// still-pending request. If another script has already registered a handler here, compose with it
// so both run and both must succeed.
//
// Field/collection-point capture also happens here (rather than on formsStartSubmitting) because
// EPiServerForms.js invokes Extension.onBeforeSubmit *before* running field/step validation, while
// formsStartSubmitting only fires later, from inside the actual AJAX submit - i.e. after this
// handler (and the OneTrust consent capture call it triggers) has already run. Relying on
// formsStartSubmitting here meant the consent capture request was always sent with stale/default
// values (module state not yet refreshed for this submit attempt).
//
// Registered as soon as Forms is ready (not deferred until the SDK has initialized and the
// touchpoints have rendered) so there is no window during which a premature submit finds no
// onBeforeSubmit handler at all and slips through unvalidated - instead, the handler itself first
// awaits the SDK and the submitted form's own touchpoint render, so the required
// checkboxes/fields are guaranteed to exist in the DOM before validation/capture runs below,
// regardless of how quickly the visitor submits. If the SDK can't be used, or that wait times out,
// forms with a OneTrust touchpoint fail closed - visibly - instead of completing without consent
// capture.
const registerOnBeforeSubmitHandler = (sdkReady: Promise<SdkReady>): void => {
  const forms = window.epi?.EPiServer?.Forms;

  if (!forms) {
    return;
  }

  const previousOnBeforeSubmit = forms.Extension.onBeforeSubmit;

  // OneTrust forms whose onBeforeSubmit is still running. Forms only disables the submit button
  // once onBeforeSubmit has resolved, and this one waits on a network call, so a double-click would
  // otherwise send the same consent twice - and, once both resolve, submit the form twice.
  const submitsInProgress = new WeakSet<HTMLFormElement>();

  const waitForTouchpoint = async (
    workingFormElement: HTMLFormElement,
    marker: HTMLElement
  ): Promise<MarsSsoCcSdkInstance> => {
    const startedAt = performance.now();
    const remainingMs = () =>
      Math.max(0, SDK_READY_TIMEOUT_MS - (performance.now() - startedAt));

    try {
      const { marsSsoCcSdk, renderedTouchpoints } = await Promise.race([
        sdkReady,
        rejectAfter(remainingMs(), "SdkInitTimeout")
      ]);

      const touchpointRendered = renderedTouchpoints.get(marker);

      if (touchpointRendered) {
        await Promise.race([
          touchpointRendered,
          rejectAfter(remainingMs(), "TouchpointRenderTimeout")
        ]);
      }

      return marsSsoCcSdk;
    } catch (e) {
      // Without the SDK-rendered checkboxes there's no consent to validate or capture, so keep
      // failing closed - but visibly, rather than leaving the visitor with a dead submit button.
      const isTimeout = e instanceof SdkTimeoutError;

      setSubmissionErrorVisible(marker, true, getConsentErrorMessage(marker, "load"));
      trackConsentCapture(workingFormElement, {
        outcome: "Blocked",
        reason:
          e instanceof SdkUnavailableError || isTimeout
            ? e.reason
            : "TouchpointRenderFailed",
        durationMs: isTimeout ? performance.now() - startedAt : undefined,
        error: e instanceof SdkUnavailableError ? e.detail : isTimeout ? undefined : e,
        errorMessageShown: "load"
      });
      throw e;
    }
  };

  const handleOneTrustSubmit = async (
    workingFormElement: HTMLFormElement,
    marker: HTMLElement,
    submitAction: HTMLElement | undefined
  ): Promise<void> => {
    const marsSsoCcSdk = await waitForTouchpoint(workingFormElement, marker);

    // Only a finalising submit captures consent, so a multi-step "Next" needn't wait for the phone
    // number to sync (which doesn't affect whether this is one - see isFinalisingSubmit).
    if (predictFormsSubmit(workingFormElement, submitAction).kind !== "not-final") {
      await waitForPhoneInputSync(workingFormElement);
    }

    captureOneTrustFieldValues(workingFormElement);
    captureAnalyticsCollectionPoint(workingFormElement);

    await submitOneTrustConsentCapture(workingFormElement, marsSsoCcSdk, submitAction);
  };

  // Anything handleOneTrustSubmit throws without having reported it (a bug, or the SDK breaking in
  // a new way) would otherwise leave the visitor with a submit button that silently does nothing,
  // and no trace of it in telemetry.
  const handleOneTrustSubmitReportingErrors = async (
    workingFormElement: HTMLFormElement,
    marker: HTMLElement,
    submitAction: HTMLElement | undefined
  ): Promise<void> => {
    const reportedFailures = countReportedFailures(workingFormElement);

    try {
      await handleOneTrustSubmit(workingFormElement, marker, submitAction);
    } catch (e) {
      if (countReportedFailures(workingFormElement) === reportedFailures) {
        console.warn("OneTrust consent capture failed unexpectedly:", e);
        setSubmissionErrorVisible(marker, true, getConsentErrorMessage(marker, "save"));
        trackConsentCapture(workingFormElement, {
          outcome: "Blocked",
          reason: "UnexpectedError",
          error: e,
          errorMessageShown: "save"
        });
      }

      throw e;
    }
  };

  forms.Extension.onBeforeSubmit = async ($workingForm: any) => {
    const workingFormElement: HTMLElement | null =
      $workingForm && typeof $workingForm.get === "function"
        ? $workingForm.get(0)
        : ($workingForm ?? null);

    // Taken before anything asynchronous, and for every attempt (including one dropped below), so
    // it's always this attempt's button - see takeSubmitAction.
    const submitAction =
      workingFormElement instanceof HTMLFormElement
        ? takeSubmitAction(workingFormElement)
        : undefined;

    const marker = workingFormElement?.querySelector<HTMLElement>(
      "[data-ssocc-touchpoint]"
    );

    // No OneTrust touchpoint: nothing to validate or capture.
    if (!marker || !(workingFormElement instanceof HTMLFormElement)) {
      await previousOnBeforeSubmit?.($workingForm);
      return;
    }

    if (submitsInProgress.has(workingFormElement)) {
      // Rejecting makes Forms drop this submit silently; the one in progress carries on.
      trackConsentCapture(workingFormElement, {
        outcome: "Skipped",
        reason: "SubmitInProgress"
      });
      throw new Error(
        "OneTrust consent capture: a submit of this form is already in progress"
      );
    }

    submitsInProgress.add(workingFormElement);

    try {
      await previousOnBeforeSubmit?.($workingForm);
      await handleOneTrustSubmitReportingErrors(workingFormElement, marker, submitAction);
    } finally {
      submitsInProgress.delete(workingFormElement);
    }
  };
};

export const initializeOneTrustConsent = () => {
  document.addEventListener("DOMContentLoaded", () => {
    // Started straight away rather than once Forms is ready. Its failure is handled when (if) a
    // form is submitted - see registerOnBeforeSubmitHandler.
    const sdkInitialized = initializeSdk();

    sdkInitialized.catch(() => undefined);

    window.$$epiforms?.(document).ready(() => {
      // Registered before (and regardless of) the SDK: PhonenumberElementBlock also renders this
      // validator on forms without OneTrust consent (RequireE164Format), and Forms throws on a
      // validator type it can't find, leaving such forms unsubmittable if the SDK fails. The
      // OneTrust validators likewise, so they're there for a submit that waited on the SDK - one
      // the SDK blocks never reaches validation, as onBeforeSubmit runs first.
      registerPhoneNumberE164Validator();
      registerOneTrustConsentValidator();
      registerOneTrustContactRequiredValidator();

      const sdkReady = sdkInitialized.then(marsSsoCcSdk => ({
        marsSsoCcSdk,
        renderedTouchpoints: new Map(
          Array.from(
            document.querySelectorAll<HTMLElement>(
              ".EPiServerForms [data-ssocc-touchpoint]"
            )
          ).map(marker => {
            const rendered = renderConsentTouchpoint(marker, marsSsoCcSdk);

            // Handled when (if) that form is submitted - see registerOnBeforeSubmitHandler.
            rendered.catch(e => console.warn("OneTrust touchpoint failed to render:", e));

            return [marker, rendered] as const;
          })
        )
      }));

      sdkReady.catch(() => undefined);

      trackSubmitActions();
      trackFormSubmitOutcomes();
      registerOnBeforeSubmitHandler(sdkReady);
    });
  });
};
